import React, { useRef, useState, useMemo, useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
     CSidebar,
     CSidebarBrand,
     CSidebarNav,
     CNavItem,
     CButton,
     CLink,
} from '@coreui/react';
import CIcon from '@coreui/icons-react';
import { useHistory, useLocation, Link } from 'react-router-dom';

import { setSidebarShow, setAsideShow, setFlexibleModal } from '../../actions/common';
import { getListenerNav } from '../../helpers/cms/subscriber';
import { signout } from '../../helpers/auth';
import { SUBSCRIBER_PATH, LIMITATION_TYPE, SUBSCRIBER_MY_ACCOUNT_PATH, LOWERCASE_CLICK_FEATURES, COMPONENT_NAME } from '../../constants';

import { defaultSubscriberNav, subscriberMyAccountNav } from '../_nav';
import { setShowUpgradeAccountPopup, setSubscriberState, setLimitationType, toggleAsideLimitationHit, setUnsavedLookupTableModal } from '../../actions/subscriber';
import CCreateElement from '../../components/migration/CCreateElement';
import CSidebarNavTitle from '../../components/migration/CSidebarNavTitle';
import CSidebarNavDropdown from '../../components/migration/CSidebarNavDropdown';
import CSidebarNavItem from '../../components/migration/CSidebarNavItem';
import CSidebarNavDivider from '../../components/migration/CSidebarNavDivider';

const SubscriberMyAccountSidebar = () => {
     return (
          <>
               <CSidebarNav className="my-account-nav">
                    <CCreateElement
                         items={subscriberMyAccountNav}
                         components={{
                              CSidebarNavDivider,
                              CSidebarNavDropdown,
                              CSidebarNavItem,
                              CSidebarNavTitle
                         }}
                    />
                    <CNavItem className="sidebar-nav-item">
                         <div className="sidebar-nav-link" style={{display: 'flex', gap: '15px'}} onClick={signout} >
                              <CIcon icon="cil-account-logout" className="sidebar-nav-icon" />
                              Sign out
                         </div>
                    </CNavItem>
               </CSidebarNav>
          </>
     )
}

const FeatureSubNav = ({ to }) => {
     return (
          <li className="feature-note">
               <p><CLink as={Link} to={to}>Turn on features</CLink> for this Listener to generate data.</p>
          </li>
     )
}

const SubscriberSidebar = () => {
     const location = useLocation();
     const history = useHistory();
     const dispatch = useDispatch();
     const sidebarRef = useRef(null);
     const show = useSelector(state => state.theme.sidebarShow);
     const activeAccount = useSelector(state => state.subscriber.activeAccount);
     const activeListeners = useSelector(state => state.subscriber.activeListeners);
     const activeListener = useSelector(state => state.subscriber.activeListener);
     const activeFeatures = useSelector(state => state.subscriber.activeFeatures);
     const [isScrollEnd, setIsScrollEnd] = useState(false);
     let activeCustomValuesFeatures = activeFeatures.filter(feature => {
          let strToCompare = feature.code.toLowerCase();
          return strToCompare.includes('myvalues') || strToCompare.includes('customvalues');
     })
     let activeBrowserStorageFeatures = activeFeatures.filter(feature => feature.code.toLowerCase().includes('browserstorage'));
     let activeMonitoringFeatures = activeFeatures.filter(feature => feature.code.toLowerCase().includes('monitoring'));
     let activeReportingFeatures = activeFeatures.filter(feature => feature.code.toLowerCase().includes('reporting'));
     let activePopulateFeatures = activeFeatures.filter(feature => feature.code.toLowerCase().includes('populate'));
     let activeSelectorFeatures = activeFeatures.filter(feature => feature.code.toLowerCase().includes('selector'));
     let activeUserSource = activeFeatures.filter(feature => feature.code.toLowerCase() === 'usersource');
     let activeClickFeatures = activeFeatures.filter(feature => LOWERCASE_CLICK_FEATURES.includes(feature.code.toLowerCase()));
     let specificFeatures = [
          ...activeUserSource,
          ...activeClickFeatures,
          ...activeCustomValuesFeatures,
          ...activeBrowserStorageFeatures,
          ...activeMonitoringFeatures,
          ...activeReportingFeatures,
          ...activePopulateFeatures,
          ...activeSelectorFeatures
     ];
     const listenersLoading = useSelector(state => state.subscriber.loadings.listeners);
     const isLookupTableUnsaved = useSelector(state => state.subscriber.unsavedLookupTableModal.unsaved);
     let showMyAccount = Object.entries(SUBSCRIBER_MY_ACCOUNT_PATH).some(([_, value]) => location.pathname.includes(value));

     const executeSidebarScroll = () => {
          if (sidebarRef !== null && sidebarRef.current !== null) {
               sidebarRef.current.scrollIntoView();
          }
     }

     const handleClickNavMenu = () => {
          dispatch(setSubscriberState({
               activeListener: {}
          }))
     }

     const handleClickUpgradeReporting = () => {
          dispatch(toggleAsideLimitationHit());
          dispatch(setLimitationType(LIMITATION_TYPE.UPGRADE_REPORTING, false));
          dispatch(setAsideShow(true));
     }

     useEffect(() => {
          const unlisten = history.listen(() => {
               let pathName = history.location.pathname;
               if (pathName && pathName === `/${activeAccount.secondId}`) {
                    executeSidebarScroll();
               }
          });

          return () => unlisten();
     }, []) // eslint-disable-line react-hooks/exhaustive-deps

     const listenerNav = getListenerNav(activeListeners, specificFeatures, activeFeatures, activeListener, handleClickNavMenu);
     const listenerSettingsNav = [
          {
               _tag: 'CSidebarNavTitle',
               _children: ['LISTENER SETTINGS']
          },
          ...listenerNav
     ];

     const getNewDefaultSubscriberNav = useCallback((activeAccount) => {
          const consentNav = {
               _tag: 'CSidebarNavDropdown',
               name: 'Consent & Personal Data',
               route: SUBSCRIBER_PATH.CONSENT_LISTENER,
               icon: 'cil-layers',
               _children: [
                    {
                         _tag: 'CSidebarNavItem',
                         name: 'Consent Rules',
                         to: '/consent-rule',
                         exact: false
                    },
               ],
          };

          if (activeAccount.isFree === false) {
               consentNav._children.unshift({
                    _tag: 'CSidebarNavItem',
                    name: 'Consent Listener',
                    to: '/consent-listener',
                    exact: false
               });
          }

          if (activeAccount.id) {
               return [...defaultSubscriberNav, consentNav];
          }

          return defaultSubscriberNav;
     }, [])

     const newDefaultSubscriberNav = useMemo(() => getNewDefaultSubscriberNav(activeAccount), [activeAccount, getNewDefaultSubscriberNav]);

     const onClickUpgrade = () => {
          if (isLookupTableUnsaved) {
               dispatch(setUnsavedLookupTableModal({ show: true, onAccept: () => dispatch(setShowUpgradeAccountPopup(true)) }));
          } else {
               dispatch(setShowUpgradeAccountPopup(true));
          }
     }

     const scrollEnd = (e) => {
          const isAtTheEnd = e.target.offsetHeight + e.target.scrollTop >= e.target.scrollHeight;
          setIsScrollEnd(isAtTheEnd);
     }

     const handleClickListenerLink = () => {
          dispatch(
               setFlexibleModal({
                    show: true,
                    component: COMPONENT_NAME.LISTENER_LIBRARY,
               })
          );
     };

     return (
          <CSidebar
               visible={show}
               onVisibleChange={(val) => dispatch(setSidebarShow(val))}
               className={`sidebar-left ${showMyAccount ? 'subscriber-my-account-sidebar' : ''}`}
               position="fixed"
               placement="start"
               size="lg"
          >
               <CSidebarBrand
                    style={{ display: 'flex', flex: '0 0 56px', alignItems: 'center' }}
                    className="d-md-down-none"
                    to={`/${activeAccount.secondId}`}
               >
                    {showMyAccount ? (
                         <CIcon className="sidebar-brand-full" icon="logo-dark" height={35} />
                    ) : (
                         <CIcon className="sidebar-brand-full" icon="logo-light" height={35} />
                    )}
               </CSidebarBrand>
               {showMyAccount ? (
                    <SubscriberMyAccountSidebar />
               ) : (
                    <>
                         {activeAccount.id && (
                              <>
                                   <CSidebarNav onScroll={scrollEnd}>
                                        <div ref={sidebarRef}></div>
                                        <CCreateElement
                                             items={newDefaultSubscriberNav}
                                             components={{
                                                  CSidebarNavDivider,
                                                  CSidebarNavDropdown,
                                                  CSidebarNavItem,
                                                  CSidebarNavTitle,
                                             }}
                                        />
                                        <CSidebarNavDivider />
                                        {listenersLoading ? (
                                             <div className="text-center"></div>
                                        ) : (
                                             <>
                                                  <CCreateElement
                                                       items={listenerSettingsNav}
                                                       components={{
                                                            CSidebarNavDivider,
                                                            CSidebarNavDropdown,
                                                            CSidebarNavItem,
                                                            CSidebarNavTitle,
                                                            FeatureSubNav,
                                                       }}
                                                  />
                                                  {listenerNav.length === 0 && (
                                                       <CNavItem className="px-3 d-compact-none c-d-minimized-none nav-listener-setting">
                                                            <CLink onClick={handleClickListenerLink} className="text-primary">
                                                                 Turn on listeners
                                                            </CLink>{' '}
                                                            under integrations and then manage them here.
                                                       </CNavItem>
                                                  )}
                                             </>
                                        )}
                                        <li className="sidebar-nav-title">EVENT REPORTING</li>

                                        <li className="sidebar-nav-item">
                                             {activeAccount.packageEventReporting ? (
                                                  <CLink as={Link} className="nav-item sidebar-nav-link" to={SUBSCRIBER_PATH.GLOBAL_REPORTING}>
                                                       <CIcon icon="cil-graph" className="sidebar-nav-icon" />
                                                       Global Reporting
                                                  </CLink>
                                             ) : (
                                                  <div className="upgrade-reporting">
                                                       <p>
                                                            <button onClick={handleClickUpgradeReporting}>Upgrade</button> your account for data layer
                                                            reporting
                                                       </p>
                                                  </div>
                                             )}
                                        </li>
                                   </CSidebarNav>
                                   <CSidebarNav className={`system-utilization ${isScrollEnd ? '' : 'scroll-footer'}`}>
                                        <div>
                                             <CNavItem className="p-3 btn-packages">
                                                  <CButton className="rounded-pill" color="success w-100" onClick={onClickUpgrade}>
                                                       Upgrade
                                                  </CButton>
                                                  <CButton className="btn-compare text-capitalize" onClick={onClickUpgrade}>
                                                       Compare Plans
                                                  </CButton>
                                             </CNavItem>
                                        </div>
                                   </CSidebarNav>
                              </>
                         )}
                    </>
               )}
          </CSidebar>
     );
}

export default React.memo(SubscriberSidebar);
